import React, { useEffect, useState, useRef } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { Drawer, Layout, Modal, Popover } from "antd";
import { Scrollbars } from "react-custom-scrollbars-2";
import { FaSquarePlus } from "react-icons/fa6";
import { has, startCase } from "lodash";
import SubjectDetailsSidebar from "./SubjectDetailsSidebar";
import HeaderBar from "./Header";
import WorkersForm from "@src/views/components/workers/worker-details-form/WorkerDetailsForm";
import DeviceForm from "@src/views/components/devices/device-details-form.tsx/DeviceDetailsForm";
import AdminSideBar from "./AdminSidebar";
import CustomSideBar from "./CustomSidebar";
import Filters from "@src/views/components/filters/Filters";
import RoleMenu from "@src/views/components/roles/create-role/RoleMenu";
import RoleForm from "@src/views/components/roles/create-role/RoleForm";
import ClientKitForm from "@src/views/components/clients/client-kit/client-kit-form/ClientKitForm";
import {
  AppDispatch,
  dynamicClear,
  dynamicSet,
  getChildOrganizationList,
  getKitListRequest,
  getOrganization,
  getOrganizationRequest,
  getSubjectClear,
  Organization,
  useDynamicSelector } from
"@src/redux";

import { useDispatch, useSelector } from "react-redux";

import SubjectsForm from "@src/views/components/subjects/subjects-form/SubjectsForm";
import CommonPagination from "@src/views/components/filters/Pagination";
import { Sidebar } from "react-pro-sidebar";
import { SIDEBAR_IMAGE } from "@src/helpers/imagesConstants";
import { FaFilter } from "react-icons/fa";
import ClientMenu from "@src/views/components/clients/ClientMenu";
import { useCheckPageComponentAccess } from "@src/hooks/index";
import { retrieveItem } from "@src/helpers/functions";
import { MdFileUpload } from "react-icons/md";
var pluralize = require("pluralize");

const CustomLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams<{id: string;}>();

  const [sideBarModal, setSideBarModal] = useState<boolean>(false);
  const [showTitle, setShowTitle] = useState<boolean>(false);
  const [title, setTitle] = useState<string>("");
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [filterModalOpen, setFilterModalOpen] = useState<boolean>(false);
  const dispatch = useDispatch<AppDispatch>();
  const roleMenuRef = useRef<HTMLDivElement | null>(null);
  const { myOrgResult }: {myOrgResult: Organization;} =
  retrieveItem("my_org_result") || {};
  const { organizationLevel } = useDynamicSelector("get_organization_level");
  const { result: org } = useSelector(getOrganization);
  const { state, pathname, key, hash } = useLocation();

  // const client_name = org?.name ? ` / ${startCase(org.name)}` : " ";
  const [clientName, setClientName] = useState<string>("");

  useEffect(() => {
    if (!pathname.startsWith("/settings/client-kits/")) return;

    const id = pathname.split("/")[3];
    if (id) {
      dispatch(getOrganizationRequest({ filter: { id } }));
    }
  }, [pathname, dispatch]);

  useEffect(() => {
    if (org?.name && pathname.startsWith("/settings/client-kits/")) {
      setClientName(` / ${startCase(org.name)}`);
    } else {
      setClientName("");
    }
  }, [org, pathname]);

  const cardTitle = (): string => {
    const [, cardPathTitle] = location?.pathname?.split("/settings/");
    const { pathname } = location;

    if (pathname.includes("/settings/org")) {
      return pluralize.singular(
        organizationLevel?.name ||
        myOrgResult?.level?.childLevelList?.[0]?.name ||
        "",
        "single"
      );
    } else if (pathname.includes("subjects")) {
      return pluralize.singular(
        myOrgResult?.rootOrganization?.subjectLabel || ""
      );
    } else if (cardPathTitle) {
      return startCase(cardPathTitle.split("/")?.[0]);
    } else {
      return "";
    }
  };

  const toggle = () => {
    setSideBarModal(!sideBarModal);
  };
  const handleCloseSidebar = () => {
    setSideBarModal(false);
  };
  const handleMenuClick = () => {
    setMenuOpen(false);
  };
  const handleIconClick = () => {
    if (cardTitle() === "Role") {
      setIsModalVisible(false);
      setMenuOpen(true);
    }
    let pathname = location?.pathname.split("/");
    if (pathname?.[2] === "clients") {
      navigate("/settings/client");
    } else {
      setIsModalVisible(true);
    }
  };

  const getKitLists = () => {
    dispatch(
      getKitListRequest({
        filter: {
          searchString: "",
          // operatorId: "",
          organizationId: id
        }
      })
    );
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    dispatch(getSubjectClear());
  };
  const handleScroll = (value: {scrollTop: number;}) => {
    const scroll = value.scrollTop;
    setShowTitle(scroll > 40);
  };
  const handleManageFilterSideBar = () => {
    setFilterModalOpen(true);
  };
  const handleCloseFilterSideBar = () => {
    setFilterModalOpen(false);
  };

  useEffect(() => {
    const parts = document.title.split("|");
    const place = parts[0].trim();
    setTitle(place);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
      roleMenuRef.current &&
      !roleMenuRef.current.contains(event.target as Node))
      {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [roleMenuRef]);

  const renderForm = (): React.ReactNode => {
    switch (cardTitle()) {
      case pluralize.singular(
        organizationLevel?.name ||
        myOrgResult?.level?.childLevelList?.[0]?.name ||
        ""
      ):
        return (
          <WorkersForm handleCancel={handleCancel} mutationType="add" />);

      case "Devices":
        return <DeviceForm handleCancel={handleCancel} mutationType="add" />;

      case "Client Kits":
        return (
          <ClientKitForm
            handleCancel={handleCancel}
            mutationType="add"
            modalType={"add"}
            getKitLists={getKitLists} />);

      case "Device":
        return <DeviceForm handleCancel={handleCancel} mutationType="add" />;
      case "Role":
        return <RoleForm onCancel={handleCancel} mutationType="add" />;
      case pluralize.singular(
        myOrgResult?.rootOrganization?.subjectLabel || ""
      ):
        return (
          <SubjectsForm handleCancel={handleCancel} mutationType="add" />);

      default:
        return null;
    }
  };

  const renderSidebar = (): React.ReactNode => {
    if (location.pathname.startsWith("/details/")) {
      return <SubjectDetailsSidebar />;
    }
    if (location.pathname === "/settings/home") {
      return <AdminSideBar />;
    }
    return (
      <CustomSideBar
        sideBarModal={sideBarModal}
        onCloseSideBar={handleCloseSidebar} />);

  };

  const renderAddAction = () => {
    if (
    useCheckPageComponentAccess("add_client_kit_action") &&
    cardTitle() === "Clients")
    {
      return (
        <Popover
          content={
          cardTitle() === "Clients" ?
          <ClientMenu /> :

          <RoleMenu
            setIsModalVisible={setIsModalVisible}
            onMenuClick={handleMenuClick} />}

          trigger="click"
          visible={menuOpen}
          onVisibleChange={(visible) => setMenuOpen(visible)}>
          
          <FaSquarePlus size={35} color="#197e00" className="cursor" />
        </Popover>);

    } else if (
    useCheckPageComponentAccess("add_role_action") &&
    cardTitle() === "Roles")
    {
      return (
        <Popover
          content={
          <RoleMenu
            setIsModalVisible={setIsModalVisible}
            onMenuClick={handleMenuClick} />}

          trigger="click"
          visible={menuOpen}
          onVisibleChange={(visible) => setMenuOpen(visible)}>
          
          <FaSquarePlus size={35} color="#197e00" className="cursor" />
        </Popover>);

    } else if (
    cardTitle() ===
    pluralize.singular(
      myOrgResult?.rootOrganization?.subjectLabel || ""
    ) &&
    useCheckPageComponentAccess("create_subject_action") ||
    cardTitle() ===
    pluralize.singular(
      organizationLevel?.name ||
      myOrgResult?.level?.childLevelList?.[0]?.name ||
      "",
      "single"
    ) &&
    useCheckPageComponentAccess("create_subject_action") ||
    cardTitle() === "Devices" &&
    useCheckPageComponentAccess("add_device_action") ||
    cardTitle() === "Client Kits" &&
    useCheckPageComponentAccess("add_client_kit_action"))
    {
      return (
        <>
          {location.pathname.includes("/settings/subjects") &&
          <MdFileUpload
            size={35}
            color="#197e00"
            className="cursor"
            onClick={renderUploadAction}
            style={{ cursor: "pointer" }} />}

          

          <FaSquarePlus
            size={35}
            color="#197e00"
            className="cursor"
            onClick={handleIconClick} />
          
        </>);

    } else {
      return null;
    }
  };

  const renderUploadAction = () => {
    if (location.pathname.includes("/settings/subjects"))
    navigate("/settings/bulk-upload");
  };

  const handleBackwardClient = (record: {id: string;name: string;}) => {
    let filterBreadCrumbs = state?.breadCrumbs.filter(
      (item: any) => item.id === record.id
    );

    dispatch(
      dynamicSet("get_organization_level", {
        organizationLevel: {
          name: record?.name.split("-")?.[1]
        }
      })
    );
    navigate(`/settings/org/${record.id}`, {
      state: { breadCrumbs: filterBreadCrumbs }
    });
  };
  const handleHomeRedirect = () => {
    dispatch(dynamicClear("get_organization_level"));
    if (location.pathname.includes("/settings/org")) {
      navigate(`/settings/org/${myOrgResult?.id}`);
    }
  };

  return (
    <div style={{ padding: "3px" }}>
      <Layout className="layout">
        <Sidebar
          collapsed={sideBarModal}
          className="side-bar"
          image={SIDEBAR_IMAGE}>
          
          {renderSidebar()}
        </Sidebar>
        <div className="header_and_content_container">
          <HeaderBar
            title={title}
            showTitle={showTitle}
            toggle={toggle}
            setTitle={setTitle} />
          

          <div className="custom-layout-main-content">
            <div className="custom-layout-header-content">
              <div className="custom-layout-header">
                <span className="custom-layout-title">
                  <span
                    onClick={handleHomeRedirect}
                    style={{ cursor: "pointer" }}>
                    
                    {pluralize(cardTitle())} {clientName}
                  </span>
                  {state?.breadCrumbs?.length > 0 &&
                  state?.breadCrumbs.map(
                    (list: {id: string;name: string;}, index: number) => {
                      return (
                        <span
                          style={{
                            cursor:
                            index !== state?.breadCrumbs?.length - 1 ?
                            "pointer" :
                            "not-allowed"
                          }}
                          onClick={() =>
                          index !== state?.breadCrumbs?.length - 1 &&
                          handleBackwardClient(list)}>

                          
                            {`/ ${list.name}`}
                          </span>);

                    }
                  )}
                </span>

             

                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    flexDirection: "row",
                    alignItems: "center"
                  }}>
                  
                  {renderAddAction()}
                  <FaFilter
                    className="common-filter-icon cursor"
                    onClick={handleManageFilterSideBar}
                    size={30}
                    color="#8a164c" />
                  
                </div>
              </div>

              {/* <Scrollbars id="scrollableElement" onScrollFrame={handleScroll}>
                 <div style={{ height: "100px" }}>
                   <Outlet />
                 </div>
                </Scrollbars>
                <div className="custom-layout-footer">
                 <CommonPagination />
                </div> */}

              <Scrollbars
                id="scrollableElement"
                onScrollFrame={handleScroll}
                autoHide
                style={{ width: "100%", height: "100%" }}>
                
                <div className="custom-layout-outlet">
                  <Outlet />
                </div>
              </Scrollbars>
              <div className="custom-layout-footer">
                <CommonPagination />
              </div>
            </div>

            <div className="custom-layout-filter-content">
              <Filters />
            </div>
          </div>
        </div>
      </Layout>

      <Modal
        title={
        <span className="dynamic-modal">
            Add{" "}
            {organizationLevel?.name || organizationLevel ?
          pluralize.singular(
            organizationLevel?.name || organizationLevel,
            "single"
          ) :
          pluralize.singular(cardTitle(), "single")}
          </span>}

        open={isModalVisible}
        onCancel={handleCancel}
        destroyOnClose
        footer={null}
        width={800}>
        
        {renderForm()}
      </Modal>

      <Drawer
        title={id ? "Profile" : "Filters"}
        placement="right"
        onClose={handleCloseFilterSideBar}
        open={filterModalOpen}
        className="filter-drawer-form-body">
        
        <Filters />
      </Drawer>
      <Drawer
        title="Settings"
        placement="left"
        closable={true}
        open={sideBarModal}
        onClose={toggle}
        className="drawer-menu-form-body">
        
        {renderSidebar()}
      </Drawer>
    </div>);

};
export default CustomLayout;